import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { logger } from '../../utils/logger'
import { productApi, Product, UserLocation, reviewApi, Review } from '../../services/api'
import { ReviewItem } from '../ui/ReviewComponents'
import QuantityEditor from '../product/QuantityEditor'
import AddressModal from '../modals/AddressModal'
import { useTheme } from '../../contexts/ThemeContext'
import { useCartContext } from '../../contexts/CartContext'
import { MapPin, Store, Heart, Settings, Star } from 'lucide-react'
import { ProfileSkeleton } from '../ui/Skeleton'
import { formatPrice } from '../../utils/currency'
import PageLayout from '../layout/PageLayout'
import { useUserAvatar } from '../../hooks/useUserAvatar'
import { t } from '../../i18n/translations'

export default function Profile() {
  const navigate = useNavigate()
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null
  const user = tg?.initDataUnsafe?.user || null
  const avatarUrl = useUserAvatar()
  const { theme, toggleTheme } = useTheme()
  const { cart } = useCartContext()
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [storeLink, setStoreLink] = useState<string | null>(null)
  const [addressModalOpen, setAddressModalOpen] = useState(false)
  const [userLocation, setUserLocation] = useState<UserLocation>({
    home_country_code: null,
    home_city_name: null
  })
  const [myReviews, setMyReviews] = useState<Review[]>([])
  const [reviewsError, setReviewsError] = useState(false)

  useEffect(() => {
    if (!tg) return

    try {
      const handleBack = () => window.history.back()
      tg.BackButton?.show()
      tg.BackButton?.onClick(handleBack)

      return () => {
        tg.BackButton?.offClick(handleBack)
      }
    } catch (error) {
      console.warn('Failed to setup back button:', error)
    }

    try {
      tg.MainButton?.hide()
    } catch (error) {
      console.warn('Failed to hide main button:', error)
    }
  }, [tg])

  // Fetch user's products and store link
  useEffect(() => {
    const fetchData = async () => {
      // apiRequest handles auth automatically — just call the API
      try {
        logger.info('Profile: Fetching products, user:', user?.id || 'from JWT')

        const data = await productApi.getMyProducts()
        logger.debug('Profile: Products fetched successfully', { count: data.products?.length || 0 })
        setProducts(data.products || [])

        try {
          const storeLinkData = await productApi.getMyStoreLink()
          setStoreLink(storeLinkData.telegram_share_url)
          logger.debug('Profile: Store link fetched', storeLinkData.store_url)
        } catch (error) {
          logger.warn('Profile: Could not fetch store link:', error)
        }
      } catch (error) {
        logger.error('Profile: Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [user, tg])

  // Fetch user location
  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        const location = await productApi.getUserLocation()
        setUserLocation(location)
      } catch (error) {
        logger.warn('Profile: Failed to fetch user location:', error)
      }
    }

    fetchUserLocation()
  }, [])

  // Fetch my reviews
  useEffect(() => {
    if (!user?.id) return
    const fetchReviews = async () => {
      try {
        const tgId = user.id.toString()
        const data = await reviewApi.getUserReviews(tgId, { limit: 10 })
        setMyReviews(data.reviews || [])
        setReviewsError(false)
      } catch {
        setReviewsError(true)
      }
    }
    fetchReviews()
  }, [user?.id])

  const handleLocationSave = async (_country: string | null, _city: string | null) => {
    // Coordinates are saved directly by AddressModal via productApi.updateUserLocation
    // This callback just updates local state
    try {
      const location = await productApi.getUserLocation()
      setUserLocation(location)
    } catch (error) {
      logger.error('Profile: Failed to save location:', error)
      throw error
    }
  }

  if (loading) {
    return (
      <PageLayout>
        <ProfileSkeleton />
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      {/* Profile header with gradient */}
      <div className="-mx-4 -mt-2 mb-4 px-4 pt-6 pb-6 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-b-[2rem]">
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-xl shadow-blue-500/40 flex-shrink-0
            ring-2 ring-white/30">
            {(user?.photo_url || avatarUrl) ? (
              <img
                src={user?.photo_url || avatarUrl}
                alt={user?.first_name || 'Profile'}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold">
                {user?.first_name?.charAt(0) || user?.username?.charAt(0) || '?'}
              </div>
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-white font-bold text-lg">
              {user?.first_name || 'Welcome'}{user?.last_name ? ` ${user.last_name}` : ''}
            </h2>
            {user?.username && (
              <p className="text-blue-200 text-sm">@{user.username}</p>
            )}
            {user?.is_premium && (
              <span className="inline-block bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full text-xs font-bold mt-1">
                ⭐ Premium
              </span>
            )}
          </div>
          <button
            onClick={() => navigate('/settings')}
            className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center
              active:scale-95 transition-transform"
          >
            <Settings className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto space-y-3">

        {/* City Configuration */}
        <div className="bg-white rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-800 text-sm">My Location</h3>
            </div>
            <button
              onClick={() => setAddressModalOpen(true)}
              className="text-blue-500 text-sm font-medium"
            >
              {userLocation.home_city_name ? 'Change' : 'Set'}
            </button>
          </div>

          {userLocation.home_city_name ? (
            <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg font-medium inline-block">
              {userLocation.home_city_name}, {userLocation.home_country_code}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Set your location to help buyers find you</p>
          )}
        </div>

        {/* Share Store */}
        {storeLink && products.length > 0 && (
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <Store className="w-5 h-5 text-white" />
              <h3 className="font-semibold text-white">My Store</h3>
            </div>
            <p className="text-blue-100 text-sm mb-4">
              Share your store link with friends
            </p>
            <a
              href={storeLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (tg?.HapticFeedback) {
                  tg.HapticFeedback.impactOccurred('light')
                }
              }}
              className="w-full bg-white text-blue-600 px-4 py-3 rounded-xl font-medium flex items-center justify-center space-x-2 active:scale-98 transition-transform"
            >
              <span>📤</span>
              <span>Share My Store</span>
            </a>
          </div>
        )}

        {/* My Products */}
        <div className="bg-white rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 text-lg">📦</span>
              </div>
              <h3 className="font-semibold text-gray-800 text-sm">My Products</h3>
            </div>
            <button
              onClick={() => navigate('/product/new')}
              className="bg-blue-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center space-x-1"
            >
              <span>+</span>
              <span>Add</span>
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full w-8 h-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
              <p className="text-gray-500 text-sm">Loading...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <div className="text-3xl mb-2">📦</div>
              <p>No products yet</p>
              <p className="text-sm mt-1">Add your first product to start selling</p>
            </div>
          ) : (
            <div className="space-y-2">
              {products.map((product) => (
                <div key={product.id} className="p-3 bg-gray-50 rounded-xl flex gap-3 hover:bg-gray-100 transition-colors">
                  {/* Thumbnail */}
                  <div
                    onClick={() => navigate(`/product/${product.id}`)}
                    className="flex-shrink-0 w-14 h-14 bg-gray-200 rounded-lg overflow-hidden cursor-pointer"
                  >
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xl text-gray-400">
                        📦
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-800 truncate text-sm">{product.name}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                        product.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : product.status === 'archived'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {product.status === 'active' ? 'Active' : product.status === 'archived' ? 'Archived' : 'Draft'}
                      </span>
                    </div>

                    <p className="font-semibold text-gray-800 text-sm mb-2">
                      {product.price} {product.currency}
                    </p>

                    <div className="flex items-center gap-2">
                      <QuantityEditor
                        productId={product.id}
                        initialQuantity={product.quantity ?? 0}
                        productStatus={product.status as 'active' | 'draft' | 'archived'}
                        onUpdate={(newQuantity, newStatus) => {
                          setProducts((prev) =>
                            prev.map((p) =>
                              p.id === product.id
                                ? { ...p, quantity: newQuantity, ...(newStatus && { status: newStatus }) }
                                : p
                            )
                          )
                        }}
                        onError={(error) => {
                          logger.error('Failed to update quantity', { error, product_id: product.id })
                        }}
                      />

                      {product.status !== 'archived' && (
                        <button
                          onClick={() => navigate(`/product/${product.id}/edit`)}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg font-medium"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Favorites */}
        <div className="bg-white rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center">
                <Heart className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="font-semibold text-gray-800 text-sm">Favorites</h3>
            </div>
            {cart?.items && cart.items.length > 0 && (
              <span className="text-sm text-gray-500">{cart.items.length}</span>
            )}
          </div>

          {!cart?.items?.length ? (
            <div className="text-center py-6">
              <Heart className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm mb-3">No favorites yet</p>
              <button
                onClick={() => navigate('/products')}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                Browse Products
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {cart.items.slice(0, 3).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg cursor-pointer"
                    onClick={() => item.product?.id && navigate(`/product/${item.product.id}`)}
                  >
                    <div className="w-10 h-10 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                      {item.product?.image_url ? (
                        <img
                          src={item.product.image_url}
                          alt={item.product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-sm text-gray-400">
                          📦
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {item.product?.name}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 flex-shrink-0">
                      {formatPrice(item.product?.price || 0, item.product?.currency)}
                    </p>
                  </div>
                ))}
              </div>
              {cart.items.length > 3 && (
                <button
                  onClick={() => navigate('/cart')}
                  className="w-full mt-3 py-2 text-sm text-blue-500 font-medium"
                >
                  View all ({cart.items.length})
                </button>
              )}
            </>
          )}
        </div>

        {/* My Reviews */}
        {user?.id && (
          <div className="bg-white rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-9 h-9 rounded-xl bg-yellow-100 flex items-center justify-center">
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              </div>
              <h3 className="font-semibold text-gray-800 text-sm">My Reviews</h3>
            </div>
            {reviewsError ? (
              <p className="text-gray-500 text-sm">{t('reviews.loadError')}</p>
            ) : myReviews.length === 0 ? (
              <p className="text-gray-500 text-sm">{t('reviews.noReviews')}</p>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                {myReviews.map((r) => (
                  <ReviewItem key={r.id} review={r} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Quick Links */}
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
          <button
            onClick={() => navigate('/settings')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">⚙️</span>
              <span className="font-medium text-gray-800">Settings</span>
            </div>
            <span className="text-gray-400">→</span>
          </button>

          <div className="border-t border-gray-100" />

          <button
            onClick={toggleTheme}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{theme === 'light' ? '☀️' : '🌙'}</span>
              <span className="font-medium text-gray-800">Theme</span>
            </div>
            <span className="text-gray-500 text-sm">{theme === 'light' ? 'Light' : 'Dark'}</span>
          </button>

          <div className="border-t border-gray-100" />

          <button
            onClick={() => navigate('/page/about')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">ℹ️</span>
              <span className="font-medium text-gray-800">About</span>
            </div>
            <span className="text-gray-400">→</span>
          </button>
        </div>
      </div>

      {/* Address Modal */}
      <AddressModal
        isOpen={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        onSave={handleLocationSave}
        currentCountry={userLocation.home_country_code}
        currentCity={userLocation.home_city_name}
      />
    </PageLayout>
  )
}
